import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { getAuthProvider } from './authProviderRegistry';
import ImpersonationService from './ImpersonationService';
import { debugService } from './DebugService';
import { getScimConfig } from '../utils/envUtils';
import { stashHashRouteForMsalRedirect } from '../utils/msalRedirectUtils';
import { debugVerboseLog } from '../utils/debugLog';

const SLOW_API_THRESHOLD_MS = 1000;
const SLOW_API_TIMING_MARKER = '__hasSlowApiTimingInterceptors';

type RequestTimingMetadata = { requestStartMs?: number };

function stampRequestStartForDebug(config: InternalAxiosRequestConfig): void {
    if (!debugService.isEnabled()) return;
    const cfg = config as InternalAxiosRequestConfig & { metadata?: RequestTimingMetadata };
    cfg.metadata = { ...cfg.metadata, requestStartMs: performance.now() };
}

function resolveRequestLabel(config: InternalAxiosRequestConfig): string {
    const path = config.url ?? '';
    if (/^https?:\/\//i.test(path)) return path;
    const base = (config.baseURL ?? '').replace(/\/$/, '');
    const rel = path.startsWith('/') ? path : `/${path}`;
    return `${base}${rel}` || path || '(unknown)';
}

/** When debug mode is on, warn in the console for API calls slower than SLOW_API_THRESHOLD_MS. */
export function warnIfSlowApiRequest(
    config: InternalAxiosRequestConfig | undefined,
    status?: number
): void {
    if (!config || !debugService.isEnabled()) return;
    const start = (config as InternalAxiosRequestConfig & { metadata?: RequestTimingMetadata }).metadata?.requestStartMs;
    if (start == null) return;
    const elapsedMs = performance.now() - start;
    if (elapsedMs < SLOW_API_THRESHOLD_MS) return;
    const method = (config.method ?? 'get').toUpperCase();
    const url = resolveRequestLabel(config);
    const statusPart = status != null ? ` status=${status}` : '';
    console.warn(`[Hades API slow] ${Math.round(elapsedMs)}ms ${method} ${url}${statusPart}`);
}

function attachSlowApiTimingInterceptors(instance: AxiosInstance): void {
    if ((instance as AxiosInstance & Record<string, boolean>)[SLOW_API_TIMING_MARKER]) {
        return;
    }
    (instance as AxiosInstance & Record<string, boolean>)[SLOW_API_TIMING_MARKER] = true;

    instance.interceptors.request.use((config) => {
        stampRequestStartForDebug(config);
        return config;
    });

    instance.interceptors.response.use(
        (response) => {
            warnIfSlowApiRequest(response.config, response.status);
            return response;
        },
        (error: AxiosError) => {
            warnIfSlowApiRequest(error.config, error.response?.status);
            return Promise.reject(error);
        }
    );
}

/**
 * Shared axios instance with interceptors for token refresh and 401 retry
 */
let sharedAxiosInstance: AxiosInstance | null = null;

/**
 * Create or get the shared axios instance with interceptors
 */
export function getAxiosInstance(baseURL?: string): AxiosInstance {
    if (sharedAxiosInstance) {
        return sharedAxiosInstance;
    }

    const scimConfig = getScimConfig();
    const instance = axios.create({
        baseURL: baseURL || scimConfig.apiUrl,
        timeout: 30000,
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        }
    });

    // Request interceptor: Add fresh token to every request (auth via getAuthProvider(); no direct MSAL dependency).
    // Call getAuthProvider() inside the callback so we use the current provider (registered when AuthProvider mounts).
    instance.interceptors.request.use(
        async (config: InternalAxiosRequestConfig) => {
            const auth = getAuthProvider();
            try {
                const token = await auth.getAuthToken();
                if (token && config.headers) {
                    config.headers.Authorization = `Bearer ${token}`;
                }

                // Add impersonation and on-behalf-of headers (per-request config.meta.onBehalfOfUpn overrides footer)
                const impersonationHeaders = ImpersonationService.getImpersonationHeader();
                const requestMeta = (config as any).meta;
                const onBehalfOfUpn = requestMeta?.onBehalfOfUpn;
                const onBehalfOfHeaders = onBehalfOfUpn != null && onBehalfOfUpn !== ''
                    ? { 'x-on-behalf-of': String(onBehalfOfUpn) }
                    : ImpersonationService.getOnBehalfOfHeader();
                if (config.headers) {
                    Object.assign(config.headers, impersonationHeaders, onBehalfOfHeaders);
                }
                // When debug is on, also add x-on-behalf-of to URL params so it's visible in network tab
                const oboValue = onBehalfOfHeaders['x-on-behalf-of'];
                if (debugService.isEnabled() && oboValue) {
                    config.params = { ...config.params, 'x-on-behalf-of': oboValue };
                }
            } catch (error) {
                console.error('❌ Axios interceptor: Failed to get auth token:', error);
                if (error instanceof Error && error.message === 'AUTHENTICATION_REQUIRED') {
                    stashHashRouteForMsalRedirect();
                    auth.onAuthRequired();
                }
            }

            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    // Response interceptor: Handle 401 errors with token refresh retry
    instance.interceptors.response.use(
        (response) => {
            return response;
        },
        async (error: AxiosError) => {
            const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

            // If we get a 401 and haven't retried yet, try refreshing the token
            if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
                originalRequest._retry = true;
                const auth = getAuthProvider();

                try {
                    debugVerboseLog('🔄 Axios interceptor: 401 detected, attempting token refresh...');

                    const newToken = await auth.getAuthToken();

                    if (newToken && originalRequest.headers) {
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    }

                    return instance(originalRequest);
                } catch (refreshError) {
                    console.error('❌ Axios interceptor: Token refresh failed:', refreshError);
                    if (refreshError instanceof Error && refreshError.message === 'AUTHENTICATION_REQUIRED') {
                        stashHashRouteForMsalRedirect();
                        auth.onAuthRequired();
                    }
                    return Promise.reject(error);
                }
            }

            // For all other errors, reject normally
            return Promise.reject(error);
        }
    );

    attachSlowApiTimingInterceptors(instance);
    sharedAxiosInstance = instance;
    return instance;
}

/**
 * Create a new axios instance with interceptors (for services that need their own instance)
 */
export function createAxiosInstance(baseURL?: string, customHeaders?: Record<string, string>): AxiosInstance {
    const scimConfig = getScimConfig();
    const instance = axios.create({
        baseURL: baseURL || scimConfig.apiUrl,
        timeout: 30000,
        headers: {
            'Content-Type': customHeaders?.['Content-Type'] || 'application/json',
            'Accept': customHeaders?.['Accept'] || 'application/json',
            ...customHeaders
        }
    });

    // Request interceptor: auth via getAuthProvider() (no direct MSAL dependency); resolve provider per request
    instance.interceptors.request.use(
        async (config: InternalAxiosRequestConfig) => {
            const authForCreate = getAuthProvider();
            try {
                const token = await authForCreate.getAuthToken();
                if (token && config.headers) {
                    config.headers.Authorization = `Bearer ${token}`;
                }

                // Add impersonation and on-behalf-of headers if present (per-request meta.onBehalfOfUpn overrides footer)
                const impersonationHeaders = ImpersonationService.getImpersonationHeader();
                const requestMeta = (config as any).meta;
                const onBehalfOfUpn = requestMeta?.onBehalfOfUpn;
                const onBehalfOfHeaders = onBehalfOfUpn != null && onBehalfOfUpn !== ''
                    ? { 'x-on-behalf-of': String(onBehalfOfUpn) }
                    : ImpersonationService.getOnBehalfOfHeader();
                if (config.headers) {
                    Object.assign(config.headers, impersonationHeaders, onBehalfOfHeaders);
                }
                // When debug is on, also add x-on-behalf-of to URL params so it's visible in network tab
                const oboValue = onBehalfOfHeaders['x-on-behalf-of'];
                if (debugService.isEnabled() && oboValue) {
                    config.params = { ...config.params, 'x-on-behalf-of': oboValue };
                }
            } catch (error) {
                console.error('❌ Axios interceptor: Failed to get auth token:', error);
                if (error instanceof Error && error.message === 'AUTHENTICATION_REQUIRED') {
                    authForCreate.onAuthRequired();
                }
            }

            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    instance.interceptors.response.use(
        (response) => {
            return response;
        },
        async (error: AxiosError) => {
            const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

            if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
                originalRequest._retry = true;
                const authForCreate = getAuthProvider();

                try {
                    debugVerboseLog('🔄 Axios interceptor: 401 detected, attempting token refresh...');

                    const newToken = await authForCreate.getAuthToken();

                    if (newToken && originalRequest.headers) {
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    }

                    return instance(originalRequest);
                } catch (refreshError) {
                    console.error('❌ Axios interceptor: Token refresh failed:', refreshError);
                    if (refreshError instanceof Error && refreshError.message === 'AUTHENTICATION_REQUIRED') {
                        authForCreate.onAuthRequired();
                    }
                    return Promise.reject(error);
                }
            }

            return Promise.reject(error);
        }
    );

    attachSlowApiTimingInterceptors(instance);
    return instance;
}

